import type { Rating } from '$types/card';

export type KeyAction =
  | { kind: 'reveal' }
  | { kind: 'rate'; rating: Rating }
  | { kind: 'speak'; side: 'front' | 'back' }
  | { kind: 'fade' }
  | { kind: 'undo' }
  | { kind: 'none' };

export interface KeyContext {
  /** Whether the answer side is currently revealed. */
  revealed: boolean;
  /** Whether there is a current card to act on. */
  hasCard: boolean;
  /** Shift modifier (used to pick the speak side). */
  shiftKey?: boolean;
}

const RATING_KEYS: Record<string, Rating> = { '1': 1, '2': 2, '3': 3, '4': 4 };

/**
 * Pure keyboard-shortcut resolver for the study view.
 *
 * Critical invariant: rating keys (1–4) are ignored until the answer is
 * revealed, so a fast typist can never commit an FSRS review for a card they
 * have not yet seen.
 */
export function keyAction(key: string, ctx: KeyContext): KeyAction {
  // Undo braucht keine aktuelle Karte — nach dem letzten Review der Session
  // kann die Queue leer sein, genau dann will man den Fehltipp zurückholen.
  if (key.toLowerCase() === 'u') return { kind: 'undo' };

  if (!ctx.hasCard) return { kind: 'none' };

  if (key === ' ') return { kind: 'reveal' };

  const rating = RATING_KEYS[key];
  if (rating !== undefined) {
    return ctx.revealed ? { kind: 'rate', rating } : { kind: 'none' };
  }

  const lower = key.toLowerCase();
  if (lower === 's') return { kind: 'speak', side: ctx.shiftKey ? 'back' : 'front' };
  if (lower === 'f') return { kind: 'fade' };

  return { kind: 'none' };
}
